import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, Home, Plus, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../lib/utils';

export default function EntitiesOverviewWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [entities, setEntities] = useState({ trading: [], property: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const data = await apiRequest('/bookkeeper/entities');
      setEntities(data);
    } catch (error) {
      // Use mock data if API not available
      setEntities({
        trading: [
          { id: 1, name: 'Punjab Imports Pty Ltd', abn: '12 345 678 901', status: 'active' },
          { id: 2, name: 'Punjab Fresh Pty Ltd', abn: '98 765 432 109', status: 'active' },
          { id: 3, name: 'Punjab Wholesale Pty Ltd', abn: '55 444 333 222', status: 'active' },
        ],
        property: [
          { id: 4, name: 'Punjab Properties Trust', abn: '11 222 333 444', status: 'active' },
          { id: 5, name: 'Dhawan Family Trust', abn: '66 777 888 999', status: 'active' },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const totalEntities = entities.trading.length + entities.property.length;

  return (
    <Card className="h-full" data-testid="entities-overview-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Entities Overview
          </CardTitle>
          {expandable && expandPath && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(expandPath)}
              className="text-slate-500 hover:text-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-16 bg-slate-100 rounded-lg"></div>
            <div className="h-16 bg-slate-100 rounded-lg"></div>
          </div>
        ) : (
          <>
            {/* Trading Entities */}
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-500 rounded">
                    <TrendingUp className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-emerald-800">Trading Entities</span>
                </div>
                <span className="text-2xl font-bold text-emerald-600">{entities.trading.length}</span>
              </div>
              <div className="space-y-1">
                {entities.trading.slice(0, 2).map((entity) => (
                  <div key={entity.id} className="text-xs text-emerald-700 truncate">
                    • {entity.name}
                  </div>
                ))}
                {entities.trading.length > 2 && (
                  <div className="text-xs text-emerald-600">+{entities.trading.length - 2} more</div>
                )}
              </div>
            </div>

            {/* Property Entities */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-500 rounded">
                    <Home className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-purple-800">Property Entities</span>
                </div>
                <span className="text-2xl font-bold text-purple-600">{entities.property.length}</span>
              </div>
              <div className="space-y-1">
                {entities.property.slice(0, 2).map((entity) => (
                  <div key={entity.id} className="text-xs text-purple-700 truncate">
                    • {entity.name}
                  </div>
                ))}
                {entities.property.length > 2 && (
                  <div className="text-xs text-purple-600">+{entities.property.length - 2} more</div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="text-center pt-2 border-t">
              <span className="text-sm text-slate-500">Total: </span>
              <span className="text-sm font-semibold text-slate-700">{totalEntities} entities</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
